import { useToast } from '@app/core/notifications';
import { AuditLiveMentionConfidence } from '@mi-front/core';
import { Spinner } from '@mi-front/ui';
import {
  ThumbDownOutlined as ThumbDownOutlinedIcon,
  ThumbUp as ThumbUpIcon,
  ThumbUpOutlined as ThumbUpOutlinedIcon,
} from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { defineMessage, FormattedMessage, useIntl } from 'react-intl';

const ACCEPT_MENTION_MESSAGE = defineMessage({
  id: 'audit.liveMentions.acceptMention',
  defaultMessage: 'Aceptar mention',
});

const REJECT_MENTION_MESSAGE = defineMessage({
  id: 'audit.liveMentions.rejectMention',
  defaultMessage: 'Rechazar mention',
});

const REJECT_CONFIRM_MESSAGE = defineMessage({
  id: 'audit.liveMentions.rejectConfirm',
  defaultMessage:
    '¿Confirma rechazar mention? En tal caso, se eliminará y se reprocesará en búsqueda de mejores resultados.',
});

const REJECTED_MESSAGE = defineMessage({
  id: 'audit.liveMentions.rejected',
  defaultMessage: 'Rechazada',
});

type LiveMentionConfidenceButtonsProps = {
  confidence: AuditLiveMentionConfidence;
  isLoading: boolean;
  onConfidenceChange: (confidence: AuditLiveMentionConfidence) => void;
  className?: string;
};

/**
 * Displays the accept and reject actions for a live mention confidence.
 */
export function LiveMentionConfidenceButtons({
  confidence,
  isLoading,
  onConfidenceChange,
  className,
}: LiveMentionConfidenceButtonsProps) {
  const { $t } = useIntl();
  const { notifyInfo } = useToast();

  if (isLoading) {
    return (
      <Box className={className}>
        <Spinner />
      </Box>
    );
  }

  if (confidence === AuditLiveMentionConfidence.REJECTED) {
    return null;
  }

  const onAccept = () => {
    onConfidenceChange(
      confidence === AuditLiveMentionConfidence.ACCEPTED
        ? AuditLiveMentionConfidence.AUTO
        : AuditLiveMentionConfidence.ACCEPTED,
    );
  };

  const onReject = () => {
    if (!window.confirm($t(REJECT_CONFIRM_MESSAGE))) {
      return;
    }

    onConfidenceChange(AuditLiveMentionConfidence.REJECTED);

    notifyInfo($t(REJECTED_MESSAGE));
  };

  return (
    <Box className={className} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title={<FormattedMessage {...ACCEPT_MENTION_MESSAGE} />}>
        <IconButton size="small" color="success" onClick={onAccept}>
          {confidence === AuditLiveMentionConfidence.ACCEPTED ? (
            <ThumbUpIcon fontSize="small" />
          ) : (
            <ThumbUpOutlinedIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
      <Tooltip title={<FormattedMessage {...REJECT_MENTION_MESSAGE} />}>
        <IconButton size="small" color="error" onClick={onReject}>
          <ThumbDownOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
}
